import moment from 'moment'
import { FILE_TYPES } from '../constants/app'
import { COLOR_PALETTE } from '../constants/theme'
import { buildDropdownList } from './app/app.helpers'
import {
  checkFileExtensionRule,
  requiredRule,
  validCompleteUrlRule,
  validFutureTimeRule
} from './validation.helpers'
import { convertToDate, formatTimeToMoment, isDateSame } from './date.helpers'
import { getUpdatedFields } from './object.helpers'
import {
  COMMON_ALPHA_SORT_ORDER,
  COMMON_DATE_SORT_ORDER
} from './common.helpers'

const QUEST_SORTING_OPTIONS = {
  ...COMMON_DATE_SORT_ORDER,
  ...COMMON_ALPHA_SORT_ORDER
}

const QUEST_STATUS_OPTIONS = {
  ALL: 'all',
  ACTIVE: 'active',
  STOPPED: 'stopped'
}

const buildQuestStatusOptions = (t) => {
  return buildDropdownList(QUEST_STATUS_OPTIONS, 'quest.quest-status-types', t)
}

const buildQuestSortingOptions = (t) => {
  return buildDropdownList(
    QUEST_SORTING_OPTIONS,
    'quest.quest-sorting-types',
    t
  )
}

const buildQuestValidationRules = ({
  media,
  mediaUrl,
  titleTemp,
  amountTemp,
  descriptionTemp,
  urlTemp,
  startDateTemp,
  startDate,
  endDateTemp
}) => {
  return {
    ...(!mediaUrl && {
      mediaUrlTemp: {
        value: media,
        rules: [
          requiredRule,
          checkFileExtensionRule([
            FILE_TYPES.JPG,
            FILE_TYPES.JPEG,
            FILE_TYPES.PNG,
            FILE_TYPES.GIF
          ])
        ]
      }
    }),
    titleTemp: {
      value: titleTemp,
      rules: [requiredRule]
    },
    amountTemp: { value: amountTemp, rules: [requiredRule] },
    descriptionTemp: { value: descriptionTemp, rules: [requiredRule] },
    urlTemp: { value: urlTemp, rules: [requiredRule, validCompleteUrlRule] },
    startDateTemp: {
      value: startDateTemp,
      rules: [
        requiredRule,
        ...(!isDateSame(startDateTemp, startDate)
          ? [validFutureTimeRule()]
          : [])
      ]
    },
    endDateTemp: {
      value: endDateTemp,
      rules: [
        requiredRule,
        validFutureTimeRule(
          startDateTemp,
          'error.end-date-later-than-start',
          true
        )
      ]
    }
  }
}

const populateQuestForm = ({
  title,
  amount,
  description,
  url,
  startDate,
  endDate,
  mediaUrl
}) => {
  const startDateTemp = convertToDate(formatTimeToMoment(startDate))
  const endDateTemp = convertToDate(formatTimeToMoment(endDate))
  return {
    titleTemp: title,
    amountTemp: amount,
    descriptionTemp: description,
    urlTemp: url,
    startDateTemp,
    endDateTemp,
    // mediaUrlTemp: mediaUrl,
    title,
    amount,
    description,
    url,
    startDate: startDateTemp,
    endDate: endDateTemp,
    mediaUrl
  }
}

const questCreateFieldsFormat = (state) => {
  const questProfile = extractQuestProfile(state)
  const {
    titleTemp,
    amountTemp,
    urlTemp,
    descriptionTemp,
    startDateTemp,
    endDateTemp,
    mediaUrlTemp
  } = questProfile
  return questDataObjFormat({
    status: QUEST_STATUS_OPTIONS.ACTIVE,
    title: titleTemp,
    media_url: mediaUrlTemp,
    amount: parseInt(amountTemp),
    url: urlTemp,
    description: descriptionTemp,
    start_date: moment.utc(startDateTemp).format(),
    end_date: moment.utc(endDateTemp).format()
  })
}

const questUpdateFieldsFormat = (id, questProfile) => {
  const {
    title,
    amount,
    url,
    description,
    startDate,
    endDate,
    mediaUrl,
    titleTemp,
    amountTemp,
    urlTemp,
    descriptionTemp,
    startDateTemp,
    endDateTemp,
    mediaUrlTemp
  } = questProfile
  const data = getUpdatedFields(
    {
      title,
      media_url: mediaUrl,
      amount: parseInt(amount),
      url,
      description,
      start_date: moment.utc(startDate).format(),
      end_date: moment.utc(endDate).format()
    },
    {
      title: titleTemp,
      media_url: mediaUrlTemp || mediaUrl,
      amount: parseInt(amountTemp),
      url: urlTemp,
      description: descriptionTemp,
      start_date: moment.utc(startDateTemp).format(),
      end_date: moment.utc(endDateTemp).format()
    }
  )
  return {
    id,
    data
  }
}

const questDataObjFormat = (data) => {
  return {
    quest: {
      ...data
    }
  }
}

const buildStatusLabelColor = (status) => {
  if (status === QUEST_STATUS_OPTIONS.ACTIVE) {
    return COLOR_PALETTE.SUCCESS
  }
  return COLOR_PALETTE.ERROR
}

const questTableHeaders = (symbol) => {
  return [
    {
      value: 'title',
      label: 'quest.title'
    },
    {
      value: 'reward-amount-in',
      label: 'quest.reward-amount-in',
      args: { symbol }
    },
    {
      value: 'startDate',
      label: 'quest.quest-start-utc'
    },
    {
      value: 'endDate',
      label: 'quest.quest-end-utc'
    },
    {
      value: 'status',
      label: 'common.status',
      sx: { minWidth: 100 }
    },
    {
      value: 'actions',
      label: 'common.actions',
      sx: { textAlign: 'center' }
    }
  ]
}

const extractQuestReducer = (state) => {
  return state.value.questReducer
}

const extractQuestProfile = (state) => {
  return extractQuestReducer(state).questProfile
}

const extractQuestListProfile = (state) => {
  return extractQuestReducer(state).questListProfile
}

const isMediaAvailableForQuest = (state) => {
  const { media, mediaUrlTemp } = extractQuestProfile(state)
  return media || mediaUrlTemp
}

export {
  QUEST_SORTING_OPTIONS,
  QUEST_STATUS_OPTIONS,
  buildQuestStatusOptions,
  buildQuestSortingOptions,
  buildQuestValidationRules,
  populateQuestForm,
  questCreateFieldsFormat,
  questUpdateFieldsFormat,
  questDataObjFormat,
  buildStatusLabelColor,
  questTableHeaders,
  isMediaAvailableForQuest,
  extractQuestListProfile,
  extractQuestProfile
}
